import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Card,
  Button,
  Input,
  InputNumber,
  Table,
  Tag,
  Typography,
  Spin,
  Divider,
  Alert,
  Modal,
  Select,
} from 'antd'
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SearchOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { Popover } from 'antd'
import { toast } from 'react-toastify'
import type { PrecoMode, PrecoResolvido } from '@/types'
import { COURSE_CATEGORIES, BRAZILIAN_STATES } from '@/lib/constants'
import { usePrecosStore } from '@/stores/precosStore'

const { Title, Text } = Typography

// ─── Pure label / format helpers ──────────────────────────────────────────────

function formatBRL(valor: number | null): string {
  if (valor == null) return '—'
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

// In root mode (New Driver editing a parceiro) the delta is effectively an absolute price; in node
// mode (a pai editing its captador) it is what the pai earns on top of its own cost — labeled "Seu
// ganho" instead of the jargon "markup", so it reads clearly to a partner. AC-21.
function nounFor(mode: PrecoMode): string {
  return mode === 'root' ? 'Preço' : 'Seu ganho'
}

// Explains how the value the partner types becomes the buyer's final price.
function MarkupHelp({ mode }: { mode: PrecoMode }) {
  const content =
    mode === 'root' ? (
      <div className="max-w-xs text-sm">
        O valor informado é o <strong>preço final</strong> que o comprador paga. Você pode definir
        um preço para <strong>todos os cursos</strong>, sobrescrever por <strong>categoria</strong>{' '}
        e, se quiser, por <strong>curso</strong> específico — o mais específico vale.
      </div>
    ) : (
      <div className="max-w-xs text-sm">
        “Seu ganho” é quanto <strong>você recebe</strong> em cada venda, somado ao seu custo para
        formar o <strong>preço final</strong> do comprador. Ex.: custo R$ 69 + seu ganho R$ 20 =
        preço final R$ 89. Vale o nível mais específico: curso &gt; categoria &gt; todos os cursos.
      </div>
    )

  return (
    <Popover content={content} title="Como funciona" trigger="click">
      <Button type="text" size="small" icon={<QuestionCircleOutlined />} aria-label="Ajuda" />
    </Popover>
  )
}

function recebeLabel(mode: PrecoMode): string {
  return mode === 'root' ? 'New Driver recebe' : 'Você recebe'
}

// ─── Entry component ─────────────────────────────────────────────────────────

export function PrecosEditor() {
  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <EditorScreen />
    </div>
  )
}

// ─── Helper components ────────────────────────────────────────────────────────

function EditorScreen() {
  const { targetId } = useParams()
  const loading = usePrecosStore((s) => s.loading)
  const target = usePrecosStore((s) => s.target)
  const openEditor = usePrecosStore((s) => s.openEditor)
  const resetEditor = usePrecosStore((s) => s.resetEditor)

  useEffect(() => {
    if (targetId)
      openEditor(Number(targetId)).catch(() => toast.error('Erro ao carregar os preços'))
    return () => resetEditor()
  }, [targetId, openEditor, resetEditor])

  if (loading || !target) {
    return (
      <div className="flex justify-center py-16">
        <Spin />
      </div>
    )
  }

  return (
    <>
      <EditorHeader />
      <UfSelector />
      <EditorGrid />
      <SaveBar />
    </>
  )
}

// Scopes the whole editor to one state. Switching reloads that state's own overrides + resolved
// matrix (each state is edited independently); "Todos os estados" (null) is the national base.
function UfSelector() {
  const uf = usePrecosStore((s) => s.uf)
  const setUf = usePrecosStore((s) => s.setUf)

  return (
    <Card size="small">
      <div className="flex flex-wrap items-center gap-3">
        <Text strong>Estado (UF):</Text>
        <Select
          value={uf ?? ''}
          style={{ minWidth: 200 }}
          onChange={(value) =>
            setUf(value ? value : null).catch(() =>
              toast.error('Erro ao carregar os preços do estado')
            )
          }
          options={[
            { label: 'Todos os estados', value: '' },
            ...BRAZILIAN_STATES.map((estado) => ({ label: estado, value: estado })),
          ]}
        />
        <Text type="secondary" className="text-xs">
          Um preço definido para um estado vale só naquele estado; onde não houver, vale o de “Todos
          os estados”.
        </Text>
      </div>
    </Card>
  )
}

// A coluna de preços antigos aparece sempre que existir preço legado para o alvo — inclusive depois
// que o novo já foi salvo (spec 017). Ela some quando o salvamento acontece, que é justamente
// quando o pai ainda está conferindo se o preço novo faz sentido perto do que o captador pagava.
// Sua coluna é `auto`, então recolhê-la para a tira vertical devolve a largura aos dois painéis.
function EditorGrid() {
  const mostraLegacy = usePrecosStore((s) => !!s.legacyHint?.length)

  if (!mostraLegacy) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start mt-4">
        <LeftPane />
        <RightPane />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-4 items-start mt-4">
      <LeftPane />
      <RightPane />
      <LegacyHintColumn />
    </div>
  )
}

function EditorHeader() {
  const target = usePrecosStore((s) => s.target)!
  const navigate = useNavigate()

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/precos')} />
        <div>
          <Title level={5} className="!mb-0">
            {target.nome}
          </Title>
          <Text type="secondary">{target.cpfCnpj}</Text>
        </div>
      </div>
      <Tag color={target.mode === 'root' ? 'blue' : 'orange'}>
        {target.mode === 'root' ? 'Parceiro' : 'Captador'}
      </Tag>
    </div>
  )
}

function LeftPane() {
  const mode = usePrecosStore((s) => s.target!.mode)
  const noun = nounFor(mode)

  return (
    <Card
      title={
        <span className="flex items-center gap-1">
          Configuração
          <MarkupHelp mode={mode} />
        </span>
      }
    >
      <NivelTodos noun={noun} />
      <Divider />
      <NivelCategorias noun={noun} />
      <Divider />
      <NivelCursos noun={noun} />
    </Card>
  )
}

function LegacyHintColumn() {
  const legacyHint = usePrecosStore((s) => s.legacyHint) ?? []
  const cursos = usePrecosStore((s) => s.cursos)
  const [aberto, setAberto] = useState(false)

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        title="Mostrar preços antigos"
        className="flex h-full w-10 cursor-pointer flex-col items-center gap-3 rounded-lg border border-gray-200 bg-white py-3 text-gray-500 hover:text-[#1a3a5c]"
      >
        <DoubleLeftOutlined />
        <span className="text-xs font-medium whitespace-nowrap [writing-mode:vertical-rl]">
          Preços antigos
        </span>
      </button>
    )
  }

  const cursoById = new Map(cursos.map((curso) => [curso.id, curso]))
  const grupos = COURSE_CATEGORIES.map((categoria) => ({
    label: categoria.label,
    itens: legacyHint.filter((hint) => cursoById.get(hint.cursoId)?.categoria === categoria.value),
  })).filter((grupo) => grupo.itens.length)

  return (
    <Card
      className="lg:w-80"
      title="Preços antigos não migrados"
      extra={
        <Button
          type="text"
          size="small"
          icon={<DoubleRightOutlined />}
          onClick={() => setAberto(false)}
          title="Ocultar"
        />
      }
    >
      <Alert
        type="warning"
        showIcon
        className="!mb-4"
        message="Reconfigure os valores"
        description="Este captador tinha preços no formato antigo. Use os valores de referência abaixo para reconfigurá-los."
      />
      <div className="space-y-3">
        {grupos.map((grupo) => (
          <div key={grupo.label}>
            <Text strong className="!text-[#1a3a5c]">
              {grupo.label}
            </Text>
            <div className="mt-1">
              {grupo.itens.map((hint) => (
                <div
                  key={hint.cursoId}
                  className="flex items-center justify-between gap-3 border-b border-gray-100 py-1"
                >
                  <Text className="truncate">
                    {cursoById.get(hint.cursoId)?.nomeReduzido ?? `Curso ${hint.cursoId}`}
                  </Text>
                  <Text className="whitespace-nowrap font-medium">{formatBRL(hint.valor)}</Text>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

function NivelTodos({ noun }: { noun: string }) {
  const todos = usePrecosStore((s) => s.todos)
  const setTodos = usePrecosStore((s) => s.setTodos)

  return (
    <div>
      <Text strong>{noun} — todos os cursos</Text>
      <div className="mt-2">
        <ValorInput value={todos} onChange={setTodos} placeholder="Ex: 49,00" />
      </div>
    </div>
  )
}

function NivelCategorias({ noun }: { noun: string }) {
  const porCategoria = usePrecosStore((s) => s.porCategoria)
  const setCategoria = usePrecosStore((s) => s.setCategoria)

  return (
    <div>
      <Text strong>{noun} por categoria</Text>
      <div className="mt-2 space-y-2">
        {COURSE_CATEGORIES.map((categoria) => (
          <div key={categoria.value} className="flex items-center justify-between gap-3">
            <Text>{categoria.label}</Text>
            <ValorInput
              value={porCategoria[categoria.value] ?? null}
              onChange={(valor) => setCategoria(categoria.value, valor)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

function NivelCursos({ noun }: { noun: string }) {
  const cursos = usePrecosStore((s) => s.cursos)
  const porCurso = usePrecosStore((s) => s.porCurso)
  const setCurso = usePrecosStore((s) => s.setCurso)
  const [filtro, setFiltro] = useState('')

  const filtrados = cursos.filter((curso) =>
    curso.nomeReduzido.toLowerCase().includes(filtro.trim().toLowerCase())
  )

  return (
    <div>
      <Text strong>{noun} por curso</Text>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Filtrar cursos"
        value={filtro}
        onChange={(event) => setFiltro(event.target.value)}
        className="!my-2"
      />
      <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
        {filtrados.map((curso) => (
          <div key={curso.id} className="flex items-center justify-between gap-3">
            <Text className="truncate">{curso.nomeReduzido}</Text>
            <ValorInput
              value={porCurso[curso.id] ?? null}
              onChange={(valor) => setCurso(curso.id, valor)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

function ValorInput({
  value,
  onChange,
  placeholder,
}: {
  value: number | null
  onChange: (valor: number | null) => void
  placeholder?: string
}) {
  return (
    <InputNumber
      value={value}
      onChange={(valor) => onChange(valor == null ? null : Number(valor))}
      addonBefore="R$"
      min={0}
      precision={2}
      decimalSeparator=","
      placeholder={placeholder}
      className="!w-24"
    />
  )
}

function RightPane() {
  const mode = usePrecosStore((s) => s.target!.mode)
  const resolved = usePrecosStore((s) => s.resolved)
  const resolving = usePrecosStore((s) => s.resolving)
  const cursos = usePrecosStore((s) => s.cursos)
  const nomeById = new Map(cursos.map((curso) => [curso.id, curso.nomeReduzido]))

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          Resultado
          {resolving && <Spin size="small" />}
        </span>
      }
    >
      <div className="space-y-4">
        {COURSE_CATEGORIES.map((categoria) => {
          const itens = resolved.filter((item) => item.categoria === categoria.value)
          if (!itens.length) return null
          return (
            <GrupoResolvido
              key={categoria.value}
              label={categoria.label}
              itens={itens}
              nomeById={nomeById}
              mode={mode}
            />
          )
        })}
      </div>
    </Card>
  )
}

// One table per categoria. "Recebe" and "Preço final" sit in the same row; in root mode the two are
// always equal, so the recebe column is dropped entirely (AC — same rule as the admin read-only tab).
function GrupoResolvido({
  label,
  itens,
  nomeById,
  mode,
}: {
  label: string
  itens: PrecoResolvido[]
  nomeById: Map<number, string>
  mode: PrecoMode
}) {
  const columns = [
    {
      title: 'Curso',
      dataIndex: 'curso',
      ellipsis: true,
    },
    ...(mode === 'root'
      ? []
      : [
          {
            title: recebeLabel(mode),
            dataIndex: 'recebe',
            align: 'right' as const,
            width: 120,
          },
        ]),
    {
      title: 'Preço final',
      dataIndex: 'final',
      align: 'right' as const,
      width: 110,
    },
  ]

  const dataSource = itens.map((item) => ({
    key: item.cursoId,
    curso: nomeById.get(item.cursoId) ?? `Curso ${item.cursoId}`,
    recebe: item.faltante ? '—' : formatBRL(item.configuradorRecebe),
    final: item.faltante ? <Tag color="red">Sem preço</Tag> : formatBRL(item.precoFinal),
  }))

  return (
    <Table
      size="small"
      pagination={false}
      columns={columns}
      dataSource={dataSource}
      title={() => (
        <Text strong className="!text-[#1a3a5c]">
          {label}
        </Text>
      )}
    />
  )
}

function SaveBar() {
  const mode = usePrecosStore((s) => s.target!.mode)
  const temCaptadoresComPreco = usePrecosStore((s) => s.temCaptadoresComPreco)
  const saving = usePrecosStore((s) => s.saving)
  const salvar = usePrecosStore((s) => s.salvar)
  const [modalOpen, setModalOpen] = useState(false)

  async function persistir(manterCaptadoresIguais?: boolean) {
    try {
      await salvar(manterCaptadoresIguais)
      toast.success('Preços salvos com sucesso!')
    } catch {
      toast.error('Erro ao salvar os preços')
    }
  }

  function handleSaveClick() {
    if (mode === 'root' && temCaptadoresComPreco) setModalOpen(true)
    else persistir()
  }

  // Sticky footer so Salvar stays reachable no matter how long the price lists get. The negative
  // margins let it span the full content width past the page padding; the shadow lifts it off the
  // scrolling content behind it.
  return (
    <>
      <div className="sticky bottom-0 z-10 -mx-4 mt-2 flex justify-end border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)] md:-mx-6 md:px-6">
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveClick}>
          Salvar
        </Button>
      </div>
      <ManterCaptadoresModal
        open={modalOpen}
        saving={saving}
        onCancel={() => setModalOpen(false)}
        onApenasParceiro={async () => {
          setModalOpen(false)
          await persistir(false)
        }}
        onManterCaptadores={async () => {
          setModalOpen(false)
          await persistir(true)
        }}
      />
    </>
  )
}

function ManterCaptadoresModal({
  open,
  saving,
  onCancel,
  onApenasParceiro,
  onManterCaptadores,
}: {
  open: boolean
  saving: boolean
  onCancel: () => void
  onApenasParceiro: () => void
  onManterCaptadores: () => void
}) {
  return (
    <Modal open={open} onCancel={onCancel} title="Este parceiro tem captadores" footer={null}>
      <p>
        Alterar o preço deste parceiro muda o custo dos seus captadores. Como deseja aplicar a
        alteração?
      </p>
      <div className="flex flex-col gap-2 mt-4">
        <Button block loading={saving} onClick={onApenasParceiro}>
          Alterar apenas o parceiro
        </Button>
        <Button block type="primary" loading={saving} onClick={onManterCaptadores}>
          Alterar o parceiro e manter o preço dos captadores
        </Button>
      </div>
    </Modal>
  )
}
